/**
 * marketingTemplatesStore — Mongo-backed CRUD for user-created Content
 * Studio templates, replacing the May 2026 localStorage-only store.
 *
 * Backwards compatibility:
 *   The previous version exposed sync functions (`listTemplates`,
 *   `getTemplate`, `saveTemplate`, `deleteTemplate`, `duplicateTemplate`,
 *   `renameTemplate`) + a `useTemplatesList` React hook that returned
 *   the array directly. Existing callers don't expect Promises, but
 *   network calls are async.
 *
 *   Strategy: keep an in-memory `cache` that mirrors the server. All
 *   sync getters serve from the cache. All mutations fire-and-forget
 *   to the API and optimistically update the cache. The hook re-fetches
 *   on mount + on the same `eh-marketing-templates-changed` event the
 *   old version dispatched, so subscribers keep working unchanged.
 *
 *   localStorage is retained ONLY as a 1st-paint cache so the Library
 *   page doesn't flicker empty between mount and the first API response.
 */

import axios from "axios";

const STORAGE_KEY = "eh_marketing_templates_v1";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** In-memory mirror of server templates. Single source of truth for
 *  sync getters. Hydrated from localStorage on import, then from the API
 *  on the first `refresh()` call. */
let cache = [];
let hydrated = false;

function emitChange() {
  try { window.dispatchEvent(new Event("eh-marketing-templates-changed")); } catch { /* SSR */ }
}

function loadFromLocalStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function persistToLocalStorage(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* quota */ }
}

// Hydrate cache from localStorage immediately so the first sync read after
// page load has SOMETHING to return while the API call is in flight.
cache = loadFromLocalStorage();

function getAuthHeader() {
  // App.js stores the JWT in localStorage under `token`. If absent we let
  // axios send no Authorization header — the backend returns 401 and our
  // catch block falls back to the cached localStorage list silently.
  try {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

/** Map server doc → local cache record. The Library page expects camelCase
 *  fields like `templateId`, `variantId`, `tweaks.bgVariant`, etc. — but
 *  the backend stores snake_case (`template_id`, `variant_id`,
 *  `tweaks.bg_variant`). Translate at the boundary so neither side leaks. */
function fromServer(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const out = { ...doc };
  if ("template_id" in doc) { out.templateId = doc.template_id; delete out.template_id; }
  if ("variant_id" in doc) { out.variantId = doc.variant_id; delete out.variant_id; }
  if ("from_name" in doc) { out.fromName = doc.from_name; delete out.from_name; }
  if ("from_email" in doc) { out.fromEmail = doc.from_email; delete out.from_email; }
  if ("created_at" in doc) { out.createdAt = doc.created_at; delete out.created_at; }
  if ("updated_at" in doc) { out.updatedAt = doc.updated_at; delete out.updated_at; }
  return out;
}

function toServer(record) {
  if (!record || typeof record !== "object") return record;
  const out = { ...record };
  if ("templateId" in record) { out.template_id = record.templateId; delete out.templateId; }
  if ("variantId" in record) { out.variant_id = record.variantId; delete out.variantId; }
  if ("fromName" in record) { out.from_name = record.fromName; delete out.fromName; }
  if ("fromEmail" in record) { out.from_email = record.fromEmail; delete out.fromEmail; }
  // Drop client-managed timestamps + id — server owns them.
  delete out.createdAt; delete out.updatedAt;
  if (!out.id) delete out.id;
  // Drop owner_id if a previous payload accidentally included it.
  delete out.owner_id;
  return out;
}

/** Pull the authoritative list from the server and overwrite the cache.
 *  Falls back to leaving cache intact if the network/API is down. */
export async function refreshTemplates() {
  if (!API_BASE) return cache;
  try {
    const { data } = await axios.get(`${API_BASE}/api/marketing/templates`, {
      headers: getAuthHeader(),
    });
    const items = (data?.items || []).map(fromServer);
    cache = items;
    persistToLocalStorage(cache);
    hydrated = true;
    emitChange();
    return cache;
  } catch (e) {
    // 401/network — keep using whatever's in the cache. The Library page
    // already shows an empty state when the array is empty, so this is a
    // soft failure rather than a thrown error users see.
    return cache;
  }
}

/** Sync getter used by ContentStudio + TemplateLibrary. Returns the
 *  in-memory cache (newest-updated first). */
export function listTemplates() {
  return [...cache].sort((a, b) =>
    (b.updatedAt || "").localeCompare(a.updatedAt || "")
  );
}

export function getTemplate(id) {
  return cache.find((t) => t.id === id) || null;
}

/** Save a template. Returns the server-persisted record (with a real id
 *  + timestamps). Async — callers may await but legacy ones that don't
 *  still get an optimistic cache update + 'eh-marketing-templates-changed'
 *  event so the Library re-renders. */
export async function saveTemplate(record) {
  const headers = { ...getAuthHeader(), "Content-Type": "application/json" };
  // Always send a name + kind to satisfy server-side validation. Empty
  // names become "Untitled template" server-side.
  const payload = toServer({ name: "Untitled template", kind: "social", ...record });
  try {
    let resp;
    if (record.id) {
      // Update path — PUT replaces (server treats missing fields as no-op
      // patches for the optional ones; required ones are always sent).
      resp = await axios.put(
        `${API_BASE}/api/marketing/templates/${record.id}`,
        payload,
        { headers },
      );
    } else {
      resp = await axios.post(
        `${API_BASE}/api/marketing/templates`,
        payload,
        { headers },
      );
    }
    const saved = fromServer(resp.data);
    // Patch cache
    const idx = cache.findIndex((t) => t.id === saved.id);
    if (idx >= 0) cache[idx] = saved;
    else cache.push(saved);
    persistToLocalStorage(cache);
    emitChange();
    return saved;
  } catch (e) {
    // Offline / 401 — fall back to localStorage-only behavior so the user's
    // work isn't lost. Stamp a synthetic id + timestamps; refreshTemplates()
    // on next mount will reconcile (which means losing the offline write —
    // acceptable since we surface a toast on failure).
    const now = new Date().toISOString();
    const fallback = {
      ...record,
      id: record.id || `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: record.createdAt || now,
      updatedAt: now,
    };
    const idx = cache.findIndex((t) => t.id === fallback.id);
    if (idx >= 0) cache[idx] = fallback;
    else cache.push(fallback);
    persistToLocalStorage(cache);
    emitChange();
    return fallback;
  }
}

export async function deleteTemplate(id) {
  cache = cache.filter((t) => t.id !== id); // optimistic
  persistToLocalStorage(cache);
  emitChange();
  try {
    await axios.delete(`${API_BASE}/api/marketing/templates/${id}`, {
      headers: getAuthHeader(),
    });
  } catch {
    // Already removed locally — refreshTemplates() will reconcile if the
    // server still has it (e.g. delete was rejected).
  }
}

export async function duplicateTemplate(id) {
  try {
    const { data } = await axios.post(
      `${API_BASE}/api/marketing/templates/${id}/duplicate`,
      {},
      { headers: getAuthHeader() },
    );
    const dup = fromServer(data);
    cache.push(dup);
    persistToLocalStorage(cache);
    emitChange();
    return dup;
  } catch {
    // Fallback to local-only duplicate
    const src = getTemplate(id);
    if (!src) return null;
    const now = new Date().toISOString();
    const dup = {
      ...src,
      id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: `${src.name || "Untitled"} (copy)`,
      createdAt: now,
      updatedAt: now,
    };
    cache.push(dup);
    persistToLocalStorage(cache);
    emitChange();
    return dup;
  }
}

export async function renameTemplate(id, name) {
  const tpl = getTemplate(id);
  if (!tpl) return null;
  return saveTemplate({ ...tpl, name });
}

/** React hook — subscribes to template changes across pages. Triggers a
 *  server refresh on first mount and on every external 'changed' event. */
export function useTemplatesList() {
  // Lazy-required to avoid breaking environments that mock React.
  // eslint-disable-next-line global-require
  const { useEffect, useState } = require("react");
  const [items, setItems] = useState(() => listTemplates());
  useEffect(() => {
    let cancelled = false;
    const sync = () => { if (!cancelled) setItems(listTemplates()); };
    // First mount → pull fresh from server (only once per session).
    if (!hydrated) {
      refreshTemplates().then(sync);
    } else {
      sync();
    }
    window.addEventListener("eh-marketing-templates-changed", sync);
    window.addEventListener("storage", sync); // cross-tab
    return () => {
      cancelled = true;
      window.removeEventListener("eh-marketing-templates-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return items;
}
